import { Component, Inject, OnInit } from "@angular/core";
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { AppUppyFile, AppUploadResult, AppUppy } from "common";
import { Observable } from "rxjs";
import { BaseDialogActionData, BrandingService, ButtonColor, CommonActionKey, CompanyBranding } from 'common';
import { ProgressBarDialogService } from "./progress-bar-dialog.service";
import {
  ProgressBarDialogComponentConfig,
  ProgressBarDialogComponentResultEnum,
} from "./progress-bar.model";
import { AppSettings } from "projects/my/src/app.settings";
import { getUppyInstance, MAX_UPLOAD_FILES_COUNT } from "./document-upload-uppy-config";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";

@UntilDestroy()
@Component({
    selector: "ifm-progress-bar-dialog",
    templateUrl: "./progress-bar-dialog.component.html",
    styleUrls: ["./progress-bar-dialog.component.scss"],
    providers: [ProgressBarDialogService],
    standalone: false
})
export class ProgressBarDialogComponent implements OnInit {
  public progressValue: number;
  public phoneNumber: string;
  public failureFiles: AppUppyFile[];

  public actionsProgress: BaseDialogActionData[] = [
    { actionKey: CommonActionKey.Cancel, label: 'Cancel', buttonColor: ButtonColor.Default },
  ];

  public actionsInfo: BaseDialogActionData[] = [
    { actionKey: CommonActionKey.Cancel, label: 'Cancel', buttonColor: ButtonColor.Default },
    { actionKey: CommonActionKey.TryAgain, label: 'Try again', buttonColor: ButtonColor.Primary },
  ];

  private uppy: AppUppy;
  private recentTryFiles: AppUppyFile[];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ProgressBarDialogComponentConfig,
    private dialog: MatDialogRef<ProgressBarDialogComponent>,
    private service: ProgressBarDialogService,
    private settings: AppSettings,
    private brandingService: BrandingService,
  ) {
    this.failureFiles = [];
    this.progressValue = 0;
    this.service.setEndpointGenerator(this.data.generateEndpoint);
  }

  public ngOnInit(): void {
    this.initPhoneNumber();
    this.uppy = getUppyInstance("", "", MAX_UPLOAD_FILES_COUNT);
    this.uppy.on("progress", (progress) => {
      this.progressValue = progress;
    });

    this.recentTryFiles = this.data.filesToUpload;
    this.handleUpload(
      this.service.prepairUppyAndStartUpload(this.uppy, this.recentTryFiles)
    );
  }

  private initPhoneNumber() {
    this.brandingService.getBranding$()
      .pipe(untilDestroyed(this))
      .subscribe((branding: CompanyBranding) => {
        this.phoneNumber = branding?.phone ?? this.settings.if.contact.phone;
      })
  }

  private handleUpload(uploadPromise: Promise<AppUploadResult>): void {
    uploadPromise
      ?.then(x => this.renderUploadResult(x))
      .catch(e => this.renderUploadResult(e));
  }

  private renderUploadResult(uploadResult: AppUploadResult): void {
    if (!uploadResult) {
      return;
    }

    if (isThereAnyFailures()) {
      this.failureFiles = uploadResult.failed;
      this.progressValue = 0;
      return;
    }

    if (
      isThereAnySuccesses() &&
      isNotAllCompletedSuccessfully(this.recentTryFiles)
    ) {
      this.failureFiles = this.recentTryFiles.filter(
        (rty) => !uploadResult.successful.map((s) => s.id).includes(rty.id)
      );
      this.progressValue = 0;
      return;
    }

    if (isThereAnySuccesses()) {
      this.dialog.close(ProgressBarDialogComponentResultEnum.Completed);
      return;
    }

    console.warn("Unsupported upload result");
    return;

    function isNotAllCompletedSuccessfully(recentTryFiles) {
      return uploadResult?.successful?.length !== recentTryFiles?.length;
    }

    function isThereAnySuccesses() {
      return uploadResult?.successful?.length;
    }

    function isThereAnyFailures() {
      return uploadResult?.failed?.length;
    }
  }

  public onActionClicked(actionData: BaseDialogActionData): void {
    if (actionData.actionKey === CommonActionKey.TryAgain) {
      return this.tryAgain();
    }
    this.dialog.close(ProgressBarDialogComponentResultEnum.Canceled);
  }

  private tryAgain(): void {
    this.recentTryFiles = this.failureFiles;
    this.handleUpload(
      this.service.prepairUppyAndStartUpload(this.uppy, this.recentTryFiles)
    );
  }

  public static show(
    dialog: MatDialog,
    dialogConfig: ProgressBarDialogComponentConfig
  ): Observable<any> {
    return dialog
      .open(ProgressBarDialogComponent, {
        width: "650px",
        data: { ...dialogConfig },
        closeOnNavigation: true,
        disableClose: true,
      })
      .afterClosed();
  }
}
